import React, { useEffect, useRef, useState } from "react";
import { Button, Checkbox, Col, Collapse, Divider, Form, Input, Modal, Row, Select, Space, Spin, Tooltip, message } from "antd";
import { DeleteOutlined, EyeOutlined, MenuOutlined, MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { createSurveyTemplate, updateSurveyTemplate, addSurveyTemplateQuestions, updateSurveyTemplateQuestion, deleteSurveyTemplateQuestion } from "./educationApi";
import { CHOICE_QUESTION_TYPES, QUESTION_TYPE_OPTIONS, getSurveyClientId, loadSurveyTemplate, normalizeOptions, notifySurveyResult } from "./surveyUtils";
import { SurveyPreviewDrawer } from "./SurveyDynamicForm";

const { Option } = Select;

// Ported from the old app's surveyTemplateBuilder/TemplateFormModal.js +
// QuestionOptionsEditor.js. Drag-and-drop uses react-beautiful-dnd (the old
// app's @hello-pangea/dnd is its API-identical fork). antd 4.10 has no
// Form.useWatch, so watched values re-render via shouldUpdate Form.Items.

const QUESTION_DROPPABLE_TYPE = "QUESTION";
const OPTION_DROPPABLE_TYPE_PREFIX = "OPTION-";

const EMPTY_QUESTION = { question_text: "", question_help_text: "", question_type: "text", is_required: true, options: [] };

const RATING_SCALE_OPTIONS = [
  { label: "Excellent", value: "excellent", requires_specify: false },
  { label: "Very Good", value: "very_good", requires_specify: false },
  { label: "Good", value: "good", requires_specify: false },
  { label: "Fair", value: "fair", requires_specify: false },
  { label: "Poor", value: "poor", requires_specify: false },
];

const YES_NO_OPTIONS = [
  { label: "Yes", value: "yes", requires_specify: false },
  { label: "No", value: "no", requires_specify: false },
];

// Fixed list, not free text — "Cleaning"/"Laundry" must match exactly what
// MyMApi's legacy-field takeover (cleaning_survery_monkey_hash_code /
// laundry_survery_monkey_hash_code) matches against, case-insensitively. A
// typo here (e.g. "Cleanng") would silently break that integration.
export const SURVEY_TYPE_OPTIONS = [
  { label: "Cleaning", value: "Cleaning" },
  { label: "Laundry", value: "Laundry" },
  { label: "Other", value: "Other" },
];

const FIXED_SURVEY_TYPE_VALUES = SURVEY_TYPE_OPTIONS.filter((option) => option.value !== "Other").map((option) => option.value);

// Stored -> form fields, matching an existing survey_type case-insensitively
// against the fixed list (so the seeded lowercase "cleaning" still resolves).
const resolveSurveyTypeFieldsForForm = (rawType) => {
  const trimmed = `${rawType || ""}`.trim();
  const match = FIXED_SURVEY_TYPE_VALUES.find((value) => value.toLowerCase() === trimmed.toLowerCase());

  if (match) return { survey_type: match, survey_type_other: "" };
  if (!trimmed) return { survey_type: undefined, survey_type_other: "" };
  return { survey_type: "Other", survey_type_other: trimmed };
};

// Form fields -> the single string actually saved/displayed.
const resolveSurveyTypeValue = (values) => {
  if (values.survey_type === "Other") return `${values.survey_type_other || ""}`.trim();
  return `${values.survey_type || ""}`.trim();
};

const toOptionValue = (label, index) => {
  const slug = `${label || ""}`
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

  return slug || `option_${index + 1}`;
};

const buildQuestionsPayload = (questions = []) =>
  questions.map((question, index) => {
    const isChoiceQuestion = CHOICE_QUESTION_TYPES.includes(question.question_type);

    return {
      id: question.id === undefined || question.id === "" ? null : question.id,
      question_text: `${question.question_text || ""}`.trim(),
      question_help_text: `${question.question_help_text || ""}`.trim(),
      question_type: question.question_type,
      is_required: question.is_required ? 1 : 0,
      display_order: index + 1,
      group_key: question.group_key || "",
      options: isChoiceQuestion
        ? (question.options || []).map((option, optionIndex) => ({
            label: `${option.label || ""}`.trim(),
            value: `${option.value || ""}`.trim() || toOptionValue(option.label, optionIndex),
            requires_specify: !!option.requires_specify,
          }))
        : [],
    };
  });

const validateQuestions = (questions) => {
  if (!Array.isArray(questions) || questions.length === 0) {
    message.error("Please add at least one question.");
    return false;
  }

  const invalidChoiceQuestion = questions.find((question) => CHOICE_QUESTION_TYPES.includes(question.question_type) && (!question.options || question.options.length === 0));

  if (invalidChoiceQuestion) {
    message.error("Choice questions need at least one option.");
    return false;
  }

  return true;
};

// Nested option editor for radio/checkbox questions. Its Droppable lives inside
// the single DragDropContext owned by the modal and carries a per-question
// `type` so options can only ever be dropped back into their own question.
const QuestionOptionsEditor = ({ questionName, form }) => {
  const applyQuickFill = (presetOptions) => {
    const questions = form.getFieldValue("questions") || [];
    const nextQuestions = questions.map((question, index) => (index === questionName ? { ...(question || {}), options: presetOptions.map((option) => ({ ...option })) } : question));
    form.setFieldsValue({ questions: nextQuestions });
  };

  return (
    <>
      <Row justify="space-between" align="middle" style={{ marginBottom: 8 }}>
        <Col>
          <strong>Options</strong>
        </Col>
        <Col>
          <Space>
            <Button size="small" onClick={() => applyQuickFill(RATING_SCALE_OPTIONS)}>
              Insert 5-point scale
            </Button>
            <Button size="small" onClick={() => applyQuickFill(YES_NO_OPTIONS)}>
              Insert Yes / No
            </Button>
          </Space>
        </Col>
      </Row>

      <Form.List name={[questionName, "options"]}>
        {(fields, { add, remove }) => (
          <>
            <Droppable droppableId={`options-${questionName}`} type={`${OPTION_DROPPABLE_TYPE_PREFIX}${questionName}`}>
              {(dropProvided) => (
                <div ref={dropProvided.innerRef} {...dropProvided.droppableProps}>
                  {fields.map(({ key, name, ...restField }, index) => (
                    <Draggable key={key} draggableId={`option-${questionName}-${key}`} index={index}>
                      {(dragProvided) => (
                        <div ref={dragProvided.innerRef} {...dragProvided.draggableProps}>
                          <Row gutter={[8, 0]} align="middle">
                            <Col flex="24px" {...dragProvided.dragHandleProps} style={{ cursor: "grab", textAlign: "center", color: "#5B6472" }}>
                              <MenuOutlined />
                            </Col>
                            <Col span={8}>
                              <Form.Item {...restField} name={[name, "label"]} rules={[{ required: true, message: "Please enter the option label." }]} style={{ marginBottom: 8 }}>
                                <Input placeholder="Option label" />
                              </Form.Item>
                            </Col>
                            <Col span={6}>
                              <Form.Item {...restField} name={[name, "value"]} style={{ marginBottom: 8 }}>
                                <Input placeholder="Stored value (optional)" />
                              </Form.Item>
                            </Col>
                            <Col span={7}>
                              <Form.Item {...restField} name={[name, "requires_specify"]} valuePropName="checked" style={{ marginBottom: 8 }}>
                                <Checkbox>Needs "please specify"</Checkbox>
                              </Form.Item>
                            </Col>
                            <Col flex="32px">
                              <Form.Item style={{ marginBottom: 8 }}>
                                <Tooltip title="Remove Option">
                                  <Button size="small" danger icon={<MinusCircleOutlined />} aria-label="Remove Option" onClick={() => remove(name)} />
                                </Tooltip>
                              </Form.Item>
                            </Col>
                          </Row>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {dropProvided.placeholder}
                </div>
              )}
            </Droppable>

            <Button size="small" onClick={() => add({ label: "", value: "", requires_specify: false })} icon={<PlusOutlined />}>
              Add Option
            </Button>
          </>
        )}
      </Form.List>
    </>
  );
};

const getQuestionValue = (values, name, key) => values?.questions?.[name]?.[key];

// One question block.
const QuestionPanel = ({ field, index, form, remove, dragProvided }) => {
  const { key, name, ...restField } = field;

  return (
    <div ref={dragProvided.innerRef} {...dragProvided.draggableProps} style={{ marginBottom: 12, ...dragProvided.draggableProps.style }}>
      <Collapse defaultActiveKey={[`${key}`]}>
        <Collapse.Panel
          key={`${key}`}
          header={
            <Space>
              <span {...dragProvided.dragHandleProps} onClick={(event) => event.stopPropagation()} style={{ cursor: "grab", color: "#5B6472" }}>
                <MenuOutlined />
              </span>
              <span>
                Q{index + 1}.{" "}
                <Form.Item noStyle shouldUpdate={(prev, cur) => getQuestionValue(prev, name, "question_text") !== getQuestionValue(cur, name, "question_text")}>
                  {({ getFieldValue }) => getFieldValue(["questions", name, "question_text"]) || "Untitled question"}
                </Form.Item>
              </span>
            </Space>
          }
          extra={
            <Tooltip title="Remove Question">
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
                aria-label="Remove Question"
                onClick={(event) => {
                  event.stopPropagation();
                  remove(name);
                }}
              />
            </Tooltip>
          }
        >
          <Form.Item {...restField} name={[name, "id"]} hidden>
            <Input type="hidden" />
          </Form.Item>

          <Row gutter={[16, 0]}>
            <Col span={14}>
              <Form.Item {...restField} label="Question" name={[name, "question_text"]} rules={[{ required: true, message: "Please enter the question." }]}>
                <Input placeholder="Enter question text" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item {...restField} label="Type" name={[name, "question_type"]} rules={[{ required: true, message: "Please select a type." }]}>
                <Select placeholder="Select type">
                  {QUESTION_TYPE_OPTIONS.map((option) => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item {...restField} label="Required" name={[name, "is_required"]} valuePropName="checked">
                <Checkbox>Required</Checkbox>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 0]}>
            <Col span={24}>
              <Form.Item {...restField} label="Help Text" name={[name, "question_help_text"]}>
                <Input placeholder="Optional hint shown under the question" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item noStyle shouldUpdate={(prev, cur) => getQuestionValue(prev, name, "question_type") !== getQuestionValue(cur, name, "question_type")}>
            {({ getFieldValue }) =>
              CHOICE_QUESTION_TYPES.includes(getFieldValue(["questions", name, "question_type"])) ? <QuestionOptionsEditor questionName={name} form={form} /> : null
            }
          </Form.Item>
        </Collapse.Panel>
      </Collapse>
    </div>
  );
};

// `record` = the template row being edited, or {} / null for a new template.
const SurveyTemplateFormModal = ({ visible, record, onClose, onSaved }) => {
  const [form] = Form.useForm();
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);

  const questionOpsRef = useRef(null);
  const requestRef = useRef(0);
  const originalQuestionsRef = useRef([]); // the questions as loaded for Edit, to see what changed

  const templateId = record && record.id ? record.id : null;

  useEffect(() => {
    if (!visible) return;

    const requestId = ++requestRef.current;

    form.resetFields();
    setPreviewTemplate(null);
    originalQuestionsRef.current = [];

    if (!templateId) {
      setLoadingDetails(false);
      form.setFieldsValue({ is_active: true, questions: [{ ...EMPTY_QUESTION, options: [] }] });
      return;
    }

    setLoadingDetails(true);

    loadSurveyTemplate(templateId).then((template) => {
      if (requestRef.current !== requestId) return; // closed or reopened meanwhile
      setLoadingDetails(false);

      if (!template) return;

      const loadedQuestions = (template.questions || []).map((question) => ({
        id: question.id,
        question_text: question.question_text || "",
        question_help_text: question.question_help_text || "",
        question_type: question.question_type || "text",
        is_required: !!Number(question.is_required),
        group_key: question.group_key || "",
        options: normalizeOptions(question.options),
      }));
      originalQuestionsRef.current = buildQuestionsPayload(loadedQuestions);

      form.setFieldsValue({
        name: template.name || "",
        description: template.description || "",
        ...resolveSurveyTypeFieldsForForm(template.survey_type),
        is_active: template.is_active === undefined ? true : !!Number(template.is_active),
        questions: (template.questions || []).map((question) => ({
          id: question.id,
          question_text: question.question_text || "",
          question_help_text: question.question_help_text || "",
          question_type: question.question_type || "text",
          is_required: !!Number(question.is_required),
          group_key: question.group_key || "",
          options: normalizeOptions(question.options),
        })),
      });
    });
  }, [visible, templateId, form]);

  const handleClose = () => {
    requestRef.current += 1;
    form.resetFields();
    setPreviewTemplate(null);
    setLoadingDetails(false);
    onClose();
  };

  const handleDragEnd = (result) => {
    const { source, destination, type } = result;

    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    if (type === QUESTION_DROPPABLE_TYPE) {
      if (questionOpsRef.current && typeof questionOpsRef.current.move === "function") {
        questionOpsRef.current.move(source.index, destination.index);
      }
      return;
    }

    if (typeof type === "string" && type.indexOf(OPTION_DROPPABLE_TYPE_PREFIX) === 0) {
      const questionIndex = Number(type.slice(OPTION_DROPPABLE_TYPE_PREFIX.length));

      if (Number.isNaN(questionIndex)) return;

      const questions = form.getFieldValue("questions") || [];
      const targetQuestion = questions[questionIndex];

      if (!targetQuestion) return;

      const options = [...(targetQuestion.options || [])];
      const [movedOption] = options.splice(source.index, 1);
      options.splice(destination.index, 0, movedOption);

      form.setFieldsValue({ questions: questions.map((question, index) => (index === questionIndex ? { ...question, options } : question)) });
    }
  };

  const buildTemplatePayload = (values) => ({
    client_id: getSurveyClientId(),
    name: `${values.name || ""}`.trim(),
    description: `${values.description || ""}`.trim(),
    survey_type: resolveSurveyTypeValue(values),
    is_active: values.is_active === false ? 0 : 1,
    // a new template saves these with it; an edit saves them question by question (saveQuestionChanges)
    questions: buildQuestionsPayload(values.questions),
  });

  // Edit: the template route saves only its header, so the questions are saved
  // through the question routes: changed ones updated (a question that already
  // has answers is replaced, so past submissions keep their wording), new ones
  // added, removed ones deleted. Answers the first failure, else the last success.
  const saveQuestionChanges = async (id, questions) => {
    const original = originalQuestionsRef.current;
    const byId = Object.fromEntries(original.map((question) => [String(question.id), question]));
    const keptIds = new Set(questions.filter((question) => question.id !== null).map((question) => String(question.id)));
    const same = (a, b) => JSON.stringify({ ...a, id: null }) === JSON.stringify({ ...b, id: null });

    const calls = original.filter((question) => !keptIds.has(String(question.id))).map((question) => () => deleteSurveyTemplateQuestion(question.id));
    questions.forEach((question) => {
      if (question.id !== null && byId[String(question.id)] && !same(question, byId[String(question.id)])) {
        const { id: questionId, ...body } = question;
        calls.push(() => updateSurveyTemplateQuestion(questionId, body));
      }
    });
    const added = questions.filter((question) => question.id === null).map(({ id: _unused, ...question }) => question);
    if (added.length) calls.push(() => addSurveyTemplateQuestions(id, { questions: added }));

    let last = { error: false, result: { success: true } };
    for (const call of calls) {
      const response = await call();
      if (!response || response.error || response.result?.success === false) return response;
      last = response;
    }
    return last;
  };

  const handleSubmit = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (error) {
      return; // antd already shows the field errors
    }

    const payload = buildTemplatePayload(values);

    if (!validateQuestions(payload.questions)) return;

    setSaving(true);
    let response = templateId ? await updateSurveyTemplate(templateId, payload) : await createSurveyTemplate(payload);
    if (templateId && response && !response.error && response.result?.success !== false) {
      response = await saveQuestionChanges(templateId, payload.questions);
    }
    setSaving(false);

    if (notifySurveyResult(response, templateId ? "Survey template updated." : "Survey template added.")) {
      handleClose();
      if (typeof onSaved === "function") onSaved();
    }
  };

  const handlePreview = () => {
    const values = form.getFieldsValue();

    setPreviewTemplate({
      id: "preview",
      name: values.name || "Untitled Survey",
      description: values.description || "",
      survey_type: resolveSurveyTypeValue(values),
      questions: buildQuestionsPayload(values.questions).map((question, index) => ({ ...question, id: question.id === null ? `preview_${index}` : question.id })),
    });
  };

  return (
    <>
      <Modal
        title={templateId ? "Edit Survey Template" : "Add Survey Template"}
        visible={visible}
        onCancel={handleClose}
        width={960}
        maskClosable={false}
        forceRender
        footer={[
          <Button key="preview" icon={<EyeOutlined />} onClick={handlePreview}>
            Preview
          </Button>,
          <Button key="cancel" onClick={handleClose}>
            Cancel
          </Button>,
          <Button key="save" type="primary" loading={saving} onClick={handleSubmit}>
            Save
          </Button>,
        ]}
      >
        <Spin spinning={loadingDetails}>
          <Form form={form} layout="vertical" initialValues={{ is_active: true }}>
            <Row gutter={[16, 0]}>
              <Col span={12}>
                <Form.Item label="Template Name" name="name" rules={[{ required: true, message: "Please enter the template name." }]}>
                  <Input placeholder="e.g. Cleaning Service Satisfaction Survey" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="Survey Type" name="survey_type">
                  <Select placeholder="Select survey type" allowClear>
                    {SURVEY_TYPE_OPTIONS.map((option) => (
                      <Option key={option.value} value={option.value}>
                        {option.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item label="Active" name="is_active" valuePropName="checked">
                  <Checkbox>Active</Checkbox>
                </Form.Item>
              </Col>
            </Row>

            <Form.Item noStyle shouldUpdate={(prev, cur) => prev.survey_type !== cur.survey_type}>
              {({ getFieldValue }) =>
                getFieldValue("survey_type") === "Other" ? (
                  <Row gutter={[16, 0]}>
                    <Col span={12}>
                      <Form.Item label="Other, please specify" name="survey_type_other" rules={[{ required: true, message: "Please specify the survey type." }]}>
                        <Input placeholder="e.g. Food Safety" />
                      </Form.Item>
                    </Col>
                  </Row>
                ) : null
              }
            </Form.Item>

            <Row gutter={[16, 0]}>
              <Col span={24}>
                <Form.Item label="Description" name="description">
                  <Input.TextArea rows={2} placeholder="Optional intro shown above the questions" />
                </Form.Item>
              </Col>
            </Row>

            <Divider orientation="left" style={{ color: "#101828" }}>
              Questions
            </Divider>

            <DragDropContext onDragEnd={handleDragEnd}>
              <Form.List name="questions">
                {(fields, operations) => {
                  questionOpsRef.current = operations;

                  return (
                    <>
                      <Droppable droppableId="survey-questions" type={QUESTION_DROPPABLE_TYPE}>
                        {(dropProvided) => (
                          <div ref={dropProvided.innerRef} {...dropProvided.droppableProps}>
                            {fields.map((field, index) => (
                              <Draggable key={field.key} draggableId={`question-${field.key}`} index={index}>
                                {(dragProvided) => <QuestionPanel field={field} index={index} form={form} remove={operations.remove} dragProvided={dragProvided} />}
                              </Draggable>
                            ))}
                            {dropProvided.placeholder}
                          </div>
                        )}
                      </Droppable>

                      <Form.Item>
                        <Button onClick={() => operations.add({ ...EMPTY_QUESTION, options: [] })} icon={<PlusOutlined />}>
                          Add Question
                        </Button>
                      </Form.Item>
                    </>
                  );
                }}
              </Form.List>
            </DragDropContext>
          </Form>
        </Spin>
      </Modal>

      <SurveyPreviewDrawer visible={!!previewTemplate} onClose={() => setPreviewTemplate(null)} template={previewTemplate} />
    </>
  );
};

export default SurveyTemplateFormModal;
